import { ComponentProps, FC, useCallback, useReducer } from "react"
import { IIncomeCategory } from "src/domain/entities"
import { incomeCategoryAddService } from "src/domain/services/incomeCategory/incomeCategoryAddService"
import { useAuth } from "src/hooks"
import { FieldType } from "src/utils/enums"
import { FormikOnSubmit } from "src/utils/types"
import { IIncomeCategoryAddFormFields, IncomeCategoryAddDrawerFormView } from "."

interface Props
	extends Omit<
		ComponentProps<typeof IncomeCategoryAddDrawerFormView>,
		"handleSubmit" | "fields" | "dispatchFields"
	> {
	onSuccess: (incomeCategory: IIncomeCategory) => void
}

export const IncomeCategoryAddDrawerFormController: FC<Props> = ({
	onSuccess,
	...rest
}) => {
	const { token } = useAuth()

	const handleSubmit = useCallback<FormikOnSubmit<IIncomeCategoryAddFormFields>>(
		async (values, { setErrors }) => {
			try {
				values.fields = values.fields.map((field, i) => ({
					name: field.name,
					type: field.type,
					position: i + 1,
				}))

				const incomeCategory = await incomeCategoryAddService(values, token)
				onSuccess(incomeCategory)
				rest.onClose()
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Something went wrong"
				setErrors({ name: message })
			}
		},
		[onSuccess, rest, token],
	)

	const reducer = (
		state: {
			name: string
			type: FieldType
			position: number
		}[],
		action: {
			type: string
			index: number
			payload: {
				name: string
				type: FieldType
				position: number
			}
		},
	) => {
		switch (action.type) {
			case "add":
				return [...state, action.payload]
			case "update":
				return state.map((item, i) => {
					if (i === action.index) return action.payload
					return item
				})
			case "remove":
				if (state.length === 1) return state
				return state.filter((_, i) => i !== action.index)
			default:
				return state
		}
	}
	const [fields, dispatchFields] = useReducer(reducer, [
		{
			name: "",
			type: FieldType.STRING,
			position: 1,
		},
	])

	return (
		<IncomeCategoryAddDrawerFormView
			handleSubmit={handleSubmit}
			fields={fields}
			dispatchFields={dispatchFields}
			{...rest}
		/>
	)
}
